import * as XLSX from 'xlsx';
import { IDailySnapshot } from '../../@types/Dashboard/IDailySnapshot';

type ITransformedDailySnapshot = Record<string, any>;

const transformDataForExport = (data: IDailySnapshot[]): ITransformedDailySnapshot[] => {
  return data.map((item) => {
    const date = new Date(item.snapshot_date);
    const ano = date.getFullYear();
    const mes = date.getMonth() + 1;
    
    const frequencyMap: Record<string, string> = {
      'WEEKLY': 'Semanal',
      'DAILY': 'Diário',
      'MONTHLY': 'Mensal'
    };
    const frequency = item.micro_area_frequency ? frequencyMap[item.micro_area_frequency] || item.micro_area_frequency : '';
    
    const transformed: ITransformedDailySnapshot = {
      'Ano': ano,
      'Mês': mes,
      'Data': item.snapshot_date,
      'Fazenda': item.farm_name,
      'Retiro': item.area_name,
      'Pasto': item.micro_area_initials,
      'Lote': item.batch_initials,
      'Área do pasto (ha)': item.micro_area_acreage,
      'Tipo de Pastejo': item.micro_area_type,
      'Tipo de Monitoramento': item.micro_area_monitoring_type,
      'Valor monitoramento': item.monitor_value,
      'Valor de entrada cadastrado': item.micro_area_requirement_in,
      'Valor de saída cadastrado': item.micro_area_requirement_out,
      'Frequencia de monitoramento': frequency,
      'Tipo de movimentação': item.micro_area_grazin_begin ? 'Saída' : 'Entrada',
      '0a8 F': item.qty_0_8_female,
      '9a12 F': item.qty_9_12_female,
      '13a24 F': item.qty_13_24_female,
      '25a36 F': item.qty_25_36_female,
      '>37 F': item.qty_37_female,
      '0a8 M': item.qty_0_8_male,
      '9a12 M': item.qty_9_12_male,
      '13a24 M': item.qty_13_24_male,
      '25a36 M': item.qty_25_36_male,
      '>37 M': item.qty_37_male,
      'Touros': item.qty_adult_male || 0,
      'Quantidade de animais no lote': item.total_from_category,
      'Peso médio': item.average_weight,
      'Peso Total': item.total_weight,
      'UA': item.ua,
      'Tx lotação instantânea': item.instant_capacity_rate,
      'Produção (kg de PV)': item.cumulative_productivity,
      'Produtividade (kg de PV/ha)': item.productivity,
      'Suplementação no cocho (kg)': item.supplement_trough_quantity_total,
      'Classificação do produto no cocho': item.supplement_trough_classification,
      'Produto no cocho': item.supplement_trough_trade_name,
      'Classificação do produto no depósito 1': item.supplement_stock_classification_1,
      'Produto depósito 1': item.supplement_stock_trade_name_1,
      'Quantidade de suplemento no depósito 1': item.supplement_stock_balance_1
    };

    const itemAny = item as any;
    let productIndex = 2;
    while (
      itemAny[`supplement_stock_classification_${productIndex}`] !== undefined ||
      itemAny[`supplement_stock_trade_name_${productIndex}`] !== undefined ||
      itemAny[`supplement_stock_balance_${productIndex}`] !== undefined
    ) {
      transformed[`Classificação do produto no depósito ${productIndex}`] = itemAny[`supplement_stock_classification_${productIndex}`] || '';
      transformed[`Nome comercial produto depósito ${productIndex}`] = itemAny[`supplement_stock_trade_name_${productIndex}`] || '';
      transformed[`Quantidade de suplemento no depósito ${productIndex}`] = itemAny[`supplement_stock_balance_${productIndex}`] || 0;
      productIndex++;
    }

    return transformed;
  });
};

export const exportDailySnapshotToExcel = (data: IDailySnapshot[], filename?: string) => {
  if (!data || data.length === 0) {
    alert("Nenhum dado disponível para exportação"); 
    return;
  }

  const transformedData = transformDataForExport(data);

  // Ordenar por Data (decrescente), Fazenda, Retiro e Pasto
  transformedData.sort((a, b) => {
    // 1. Ordenar por Data (decrescente)
    const dateA = new Date(a.Data).getTime();
    const dateB = new Date(b.Data).getTime();
    if (dateB !== dateA) {
      return dateB - dateA; // Decrescente
    }

    // 2. Ordenar por Fazenda
    const fazendaA = a.Fazenda || '';
    const fazendaB = b.Fazenda || '';
    if (fazendaA !== fazendaB) {
      return fazendaA.localeCompare(fazendaB);
    }

    // 3. Ordenar por Retiro
    const retiroA = a.Retiro || '';
    const retiroB = b.Retiro || '';
    if (retiroA !== retiroB) {
      return retiroA.localeCompare(retiroB);
    }

    // 4. Ordenar por Pasto
    const pastoA = a.Pasto || '';
    const pastoB = b.Pasto || '';
    return pastoA.localeCompare(pastoB);
  });

  const worksheet = XLSX.utils.json_to_sheet(transformedData);
  
  const maxProducts = transformedData.reduce((max, row) => {
    const productKeys = Object.keys(row).filter(key => 
      key.startsWith('Classificação do produto no depósito') || 
      key.startsWith('Nome comercial produto depósito') || 
      key.startsWith('Quantidade de suplemento no depósito')
    );
    const productNumbers = productKeys.map(key => {
      const match = key.match(/depósito (\d+)/);
      return match ? parseInt(match[1]) : 0;
    });
    return Math.max(max, ...productNumbers, 0);
  }, 0);

  const columnWidths = [
    { wch: 8 },   // Ano
    { wch: 8 },   // Mês
    { wch: 12 },  // Data
    { wch: 20 },  // Fazenda
    { wch: 15 },  // Retiro
    { wch: 15 },  // Pasto
    { wch: 12 },  // Lote
    { wch: 18 },  // Área do pasto (ha)
    { wch: 15 },  // Tipo de Pastejo
    { wch: 20 },  // Tipo de Monitoramento
    { wch: 18 },  // Valor monitoramento
    { wch: 25 },  // Valor de entrada cadastrado
    { wch: 25 },  // Valor de saída cadastrado
    { wch: 25 },  // Frequencia de monitoramento
    { wch: 18 },  // Data início pastejo
    { wch: 18 },  // Data fim pastejo
    { wch: 20 },  // Tipo de movimentação
    { wch: 12 },  // 0a8 F
    { wch: 12 },  // 9a12 F
    { wch: 12 },  // 12a24 F
    { wch: 12 },  // 24a36 F
    { wch: 12 },  // >36 F
    { wch: 12 },  // 0a8 M
    { wch: 12 },  // 9a12 M
    { wch: 12 },  // 12a24 M
    { wch: 12 },  // 24a36 M
    { wch: 12 },  // >36 M
    { wch: 12 },  // Touros
    { wch: 28 },  // Quantidade de animais no lote
    { wch: 12 },  // Peso médio
    { wch: 12 },  // Peso Total
    { wch: 15 },  // UA instantânea
    { wch: 15 },  // Tx lotação
    { wch: 20 },  // Produção (kg de PV)
    { wch: 25 },  // Produtividade (kg de PV/ha)
    { wch: 28 },  // Suplementação no cocho (kg)
    { wch: 35 },  // Classificação do produto no cocho
    { wch: 20 },  // Produto no cocho
    { wch: 20 },  // Status do cocho
    { wch: 35 },  // Classificação do produto no depósito 1
    { wch: 35 },  // Nome comercial produto depósito 1
    { wch: 40 },  // Quantidade de suplemento no depósito 1
    { wch: 35 },  // Classificação do produto no depósito
    { wch: 20 },  // Produto no depósito
    { wch: 40 },  // Quantidade de suplemento no depósito do pasto
  ];

  for (let i = 2; i <= maxProducts; i++) {
    columnWidths.push(
      { wch: 35 },  // Classificação do produto no depósito N
      { wch: 35 },  // Nome comercial produto depósito N
      { wch: 40 }   // Quantidade de suplemento no depósito N
    );
  }
  
  worksheet['!cols'] = columnWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Snapshot');

  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array' 
  });

  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `daily-snapshot-${new Date().toISOString().split('T')[0]}.xlsx`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const transformDataForExportByBatch = (data: IDailySnapshot[]): ITransformedDailySnapshot[] => {
  return data.map((item) => {
    const date = new Date(item.snapshot_date);
    const ano = date.getFullYear();
    const mes = date.getMonth() + 1;
    
    const transformed: ITransformedDailySnapshot = {
      'Ano': ano,
      'Mês': mes,
      'Data': item.snapshot_date,
      'Fazenda': item.farm_name,
      'Retiro': item.area_name,
      'Lote': item.batch_initials,
      'Pasto': item.micro_area_initials,
      'Área do pasto (ha)': item.micro_area_acreage,
      '0a8 F': item.qty_0_8_female,
      '9a12 F': item.qty_9_12_female,
      '13a24 F': item.qty_13_24_female,
      '25a36 F': item.qty_25_36_female,
      '>=37 F': item.qty_37_female,
      '0a8 M': item.qty_0_8_male,
      '9a12 M': item.qty_9_12_male,
      '13a24 M': item.qty_13_24_male,
      '25a36 M': item.qty_25_36_male,
      '>=37 M': item.qty_37_male,
      'Touros': item.qty_adult_male || 0,
      'Quantidade de animais no lote': item.total_from_category,
      'Peso médio': item.average_weight,
      'Peso Total': item.total_weight,
      'UA': item.ua,
      'Tx lotação instantânea': item.instant_capacity_rate,
      'Ganho médio (kg PV/ cab)': item.productivity_average,
      'Ganho total (kg PV)': item.cumulative_productivity,
      'Produtividade (kg PV/ha)': item.productivity,
      'Suplementação no cocho (kg)': item.supplement_trough_quantity_total,
      'Classificação do produto no cocho': item.supplement_trough_classification,
      'Produto no cocho': item.supplement_trough_trade_name
    };

    return transformed;
  });
};

export const exportDailySnapshotByBatchToExcel = (data: IDailySnapshot[], filename?: string) => {
  if (!data || data.length === 0) {
    alert("Nenhum dado disponível para exportação"); 
    return;
  }

  const transformedData = transformDataForExportByBatch(data);

  // Ordenar por Data (decrescente), Fazenda, Retiro e Lote
  transformedData.sort((a, b) => {
    // 1. Ordenar por Data (decrescente)
    const dateA = new Date(a.Data).getTime();
    const dateB = new Date(b.Data).getTime();
    if (dateB !== dateA) {
      return dateB - dateA; // Decrescente
    }

    // 2. Ordenar por Fazenda
    const fazendaA = a.FAZENDA || '';
    const fazendaB = b.FAZENDA || '';
    if (fazendaA !== fazendaB) {
      return fazendaA.localeCompare(fazendaB);
    }

    // 3. Ordenar por Retiro
    const retiroA = a.Retiro || '';
    const retiroB = b.Retiro || '';
    if (retiroA !== retiroB) {
      return retiroA.localeCompare(retiroB);
    }

    // 4. Ordenar por Lote
    const loteA = a.Lote || '';
    const loteB = b.Lote || '';
    return loteA.localeCompare(loteB);
  });

  const worksheet = XLSX.utils.json_to_sheet(transformedData);
  
  const columnWidths = [
    { wch: 8 },   // Ano
    { wch: 8 },   // Mês
    { wch: 12 },  // Data
    { wch: 20 },  // FAZENDA
    { wch: 15 },  // Retiro
    { wch: 12 },  // Lote
    { wch: 15 },  // Pasto
    { wch: 18 },  // Área do pasto (ha)
    { wch: 12 },  // 0a8 F
    { wch: 12 },  // 9a12 F
    { wch: 12 },  // 13a24 F
    { wch: 12 },  // 25a36 F
    { wch: 12 },  // >37 F
    { wch: 12 },  // 0a8 M
    { wch: 12 },  // 9a12 M
    { wch: 12 },  // 13a24 M
    { wch: 12 },  // 25a36 M
    { wch: 12 },  // >37 M
    { wch: 12 },  // Touros
    { wch: 28 },  // Quantidade de animais no lote
    { wch: 12 },  // Peso médio
    { wch: 12 },  // Peso Total
    { wch: 15 },  // UA
    { wch: 20 },  // Tx lotação instantânea
    { wch: 22 },  // Ganho médio (kg PV/ cab)
    { wch: 20 },  // Ganho total (kg PV)
    { wch: 25 },  // Produtividade (kg PV/ha)
    { wch: 28 },  // Suplementação no cocho (kg)
    { wch: 35 },  // Classificação do produto no cocho
    { wch: 20 }   // Produto no cocho
  ];
  
  worksheet['!cols'] = columnWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Snapshot By Batch');

  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array' 
  });

  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `daily-snapshot-by-batch-${new Date().toISOString().split('T')[0]}.xlsx`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
